import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useWaitTimeManager } from '../hooks/useWaitTimeManager';
import { useAppDataManager } from '../hooks/useAppDataManager';
import appLifecycleManager from '../services/AppLifecycleManager';

const AppProvider = ({ children }) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useSelector(state => state.auth);
  const { startManager: startWaitTimeManagerHook, stopManager: stopWaitTimeManagerHook, isActive: isWaitTimeActive } = useWaitTimeManager();
  const { startManager: startAppDataManager, stopManager: stopAppDataManager, isActive: isAppDataActive } = useAppDataManager();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Warte bis Auth-Check abgeschlossen ist
        if (isAuthLoading) {
          console.log('AppProvider: Warte auf Authentifizierung...');
          return;
        }
        
        // Nur starten wenn User authentifiziert ist
        if (!isAuthenticated) {
          console.log('AppProvider: User nicht authentifiziert, überspringe Services');
          setIsInitialized(false);
          return;
        }
        
        console.log('AppProvider: User authentifiziert, initialisiere App-Services...');
        
        // Prüfe ob bereits initialisiert
        if (appLifecycleManager.getStatus().isInitialized) {
          console.log('AppProvider: Services bereits initialisiert, überspringe...');
          setIsInitialized(true);
          return;
        }
        
        // App-Lifecycle-Manager initialisieren
        appLifecycleManager.initializeHandlers();
        
        // AppDataManager starten (ersetzt alle einzelnen Data-Loader)
        await startAppDataManager();
        
        setIsInitialized(true);
        console.log('AppProvider: App-Services erfolgreich initialisiert');
        
      } catch (error) {
        console.error('AppProvider: Fehler bei App-Initialisierung:', error);
      }
    };

    initializeApp();

    // KEIN Cleanup bei Unmount - Services sollen während der App-Laufzeit aktiv bleiben
    // Cleanup erfolgt nur bei beforeunload Event
  }, [isAuthLoading, isAuthenticated, startAppDataManager]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wartezeiten abonnieren. Frueher wurde hier der WaitTimeManager gestartet, der
  // selbst rechnete und deshalb auf die AppData-Daten warten musste. Gerechnet wird
  // jetzt ausschliesslich serverseitig — das Abo auf `waitTimes/current` braucht
  // keine Vorbedingung ausser einem angemeldeten Nutzer und kann sofort laufen.
  // Siehe docs/KONZEPT_WARTEZEIT_RUECKBAU_INS_BACKEND.md
  useEffect(() => {
    if (!isInitialized || isWaitTimeActive) return;

    const subscribe = async () => {
      try {
        console.log('AppProvider: Abonniere Wartezeiten...');
        await startWaitTimeManagerHook();
      } catch (error) {
        console.error('AppProvider: Fehler beim Wartezeit-Abo:', error);
      }
    };

    subscribe();
  }, [isInitialized, isWaitTimeActive, startWaitTimeManagerHook]);

  // Cleanup Effect - stoppt Services wenn User sich ausloggt
  useEffect(() => {
    const cleanupServices = async () => {
      if (!isAuthenticated && !isAuthLoading && isInitialized) {
        console.log('AppProvider: User ausgeloggt, stoppe Services...');
        
        try {
          // Wartezeit-Abo beenden
          if (isWaitTimeActive) {
            await stopWaitTimeManagerHook();
          }
          
          // Stoppe AppDataManager
          if (isAppDataActive) {
            await stopAppDataManager();
          }
          
          // Reset Initialized Status
          setIsInitialized(false);
          
          console.log('AppProvider: Services erfolgreich gestoppt');
        } catch (error) {
          console.error('AppProvider: Fehler beim Stoppen der Services:', error);
        }
      }
    };
    
    cleanupServices();
  }, [isAuthenticated, isAuthLoading, isInitialized, isWaitTimeActive, isAppDataActive, stopWaitTimeManagerHook, stopAppDataManager]);

  // Debug-Information reduziert - nur bei Problemen
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && isInitialized) {
      console.log('AppProvider: Services gestartet - AppData:', isAppDataActive, 'WaitTime:', isWaitTimeActive);
    }
  }, [isInitialized, isAppDataActive, isWaitTimeActive]);

  return (
    <>
      {children}
    </>
  );
};

export default AppProvider;